/**
 * Standardized Analytics Events
 *
 * Client-safe event constants that can be used in browser and server environments
 */

// Lead Generation Events with Scoring (1-100 scale)
export const LEAD_GENERATION_EVENTS = {
	// Low-intent actions (5-25 points)
	LEAD_PAGE_VIEW: 'lead_page_view',
	LEAD_CONTENT_VIEW: 'lead_content_view',
	LEAD_PRODUCT_VIEW: 'lead_product_view',
	LEAD_GALLERY_VIEW: 'lead_gallery_view',
	LEAD_SCROLL: 'lead_scroll',
	LEAD_SCROLL_ENGAGED: 'lead_scroll_engaged',

	// Lead Form Step Events - Phone Step (30-45 points)
	LEAD_FORM_STEP1_VIEWED: 'lead_form_step1_viewed',
	LEAD_FORM_STEP1_STARTED: 'lead_form_step1_started',
	LEAD_FORM_STEP1_COMPLETED: 'lead_form_step1_completed',
	
	// Lead Form Step Events - Products Step (46-60 points)
	LEAD_FORM_STEP2_VIEWED: 'lead_form_step2_viewed',
	LEAD_FORM_STEP2_STARTED: 'lead_form_step2_started',
	LEAD_FORM_STEP2_COMPLETED: 'lead_form_step2_completed',
	
	// Lead Form Step Events - Customization Step (61-75 points)
	LEAD_FORM_STEP3_VIEWED: 'lead_form_step3_viewed',
	LEAD_FORM_STEP3_STARTED: 'lead_form_step3_started',
	LEAD_FORM_STEP3_COMPLETED: 'lead_form_step3_completed',
	
	// Lead Form Step Events - Details Step (76-90 points)
	LEAD_FORM_STEP4_VIEWED: 'lead_form_step4_viewed',
	LEAD_FORM_STEP4_STARTED: 'lead_form_step4_started',
	LEAD_FORM_STEP4_COMPLETED: 'lead_form_step4_completed',

	// Still actively used events
	LEAD_INQUIRY_STARTED: 'lead_inquiry_started',
	LEAD_QUALIFICATION: 'lead_qualification',
	LEAD_CONTACT_INFO: 'lead_contact_info',
	LEAD_WHATSAPP_CONTACT: 'lead_whatsapp_contact',

	// Conversion actions (90-100 points)
	LEAD_PHONE_CALL: 'lead_phone_call',
	LEAD_MEETING_SCHEDULED: 'lead_meeting_scheduled',
	LEAD_FORM_SUBMITTED: 'lead_form_submitted',
} as const;

// Lead Scoring Values
export const LEAD_SCORES = {
	// Low-intent (5-25)
	[LEAD_GENERATION_EVENTS.LEAD_PAGE_VIEW]: 5,
	[LEAD_GENERATION_EVENTS.LEAD_CONTENT_VIEW]: 15,
	[LEAD_GENERATION_EVENTS.LEAD_PRODUCT_VIEW]: 20,
	[LEAD_GENERATION_EVENTS.LEAD_GALLERY_VIEW]: 20,
	[LEAD_GENERATION_EVENTS.LEAD_SCROLL]: 25,
	[LEAD_GENERATION_EVENTS.LEAD_SCROLL_ENGAGED]: 25,

	// Lead Form Step 1 - Phone (30-45)
	[LEAD_GENERATION_EVENTS.LEAD_FORM_STEP1_VIEWED]: 30,
	[LEAD_GENERATION_EVENTS.LEAD_FORM_STEP1_STARTED]: 35,
	[LEAD_GENERATION_EVENTS.LEAD_FORM_STEP1_COMPLETED]: 45,
	
	// Lead Form Step 2 - Products (46-60)
	[LEAD_GENERATION_EVENTS.LEAD_FORM_STEP2_VIEWED]: 46,
	[LEAD_GENERATION_EVENTS.LEAD_FORM_STEP2_STARTED]: 50,
	[LEAD_GENERATION_EVENTS.LEAD_FORM_STEP2_COMPLETED]: 60,
	
	// Lead Form Step 3 - Customization (61-75)
	[LEAD_GENERATION_EVENTS.LEAD_FORM_STEP3_VIEWED]: 61,
	[LEAD_GENERATION_EVENTS.LEAD_FORM_STEP3_STARTED]: 65,
	[LEAD_GENERATION_EVENTS.LEAD_FORM_STEP3_COMPLETED]: 75,
	
	// Lead Form Step 4 - Details (76-90)
	[LEAD_GENERATION_EVENTS.LEAD_FORM_STEP4_VIEWED]: 76,
	[LEAD_GENERATION_EVENTS.LEAD_FORM_STEP4_STARTED]: 80,
	[LEAD_GENERATION_EVENTS.LEAD_FORM_STEP4_COMPLETED]: 90,

	// Still actively used events
	[LEAD_GENERATION_EVENTS.LEAD_INQUIRY_STARTED]: 40,
	[LEAD_GENERATION_EVENTS.LEAD_QUALIFICATION]: 50,
	[LEAD_GENERATION_EVENTS.LEAD_CONTACT_INFO]: 60,
	[LEAD_GENERATION_EVENTS.LEAD_WHATSAPP_CONTACT]: 85,

	// Conversion (90-100)
	[LEAD_GENERATION_EVENTS.LEAD_PHONE_CALL]: 90,
	[LEAD_GENERATION_EVENTS.LEAD_MEETING_SCHEDULED]: 95,
	[LEAD_GENERATION_EVENTS.LEAD_FORM_SUBMITTED]: 100,
} as const;

// E-commerce Events (GA4 Standard)
export const ECOMMERCE_EVENTS = {
	// Discovery
	VIEW_ITEM: 'view_item',
	VIEW_ITEM_LIST: 'view_item_list',
	SELECT_ITEM: 'select_item',
	VIEW_PROMOTION: 'view_promotion',
	SELECT_PROMOTION: 'select_promotion',

	// Shopping behavior
	ADD_TO_CART: 'add_to_cart',
	REMOVE_FROM_CART: 'remove_from_cart',
	VIEW_CART: 'view_cart',
	ADD_TO_WISHLIST: 'add_to_wishlist',

	// Checkout process
	BEGIN_CHECKOUT: 'begin_checkout',
	ADD_PAYMENT_INFO: 'add_payment_info',
	ADD_SHIPPING_INFO: 'add_shipping_info',

	// Transaction
	PURCHASE: 'purchase',
	REFUND: 'refund',
} as const;

// Export types
export type LeadGenerationEvent =
	(typeof LEAD_GENERATION_EVENTS)[keyof typeof LEAD_GENERATION_EVENTS];
export type EcommerceEvent = (typeof ECOMMERCE_EVENTS)[keyof typeof ECOMMERCE_EVENTS];
export type StandardizedEvent = LeadGenerationEvent | EcommerceEvent;
